import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ProductionAssignedFragment,
  useReservedResourcesCheckOutMutation,
  CheckinCheckoutReservedResourceInput,
  useReservedResourceUpdateMutation,
  ReservationDetailQueryVariables,
  ReservationDetailQuery,
  ReservationDetailDocument,
  ReservedResourcePhase,
  ResourceOccupancyInput,
  GuestOccupancyInput,
} from 'src/api/api.generated'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import { compareDates } from 'src/utils/date'
import { BillF, DepositF, ReservationF, ReservedResourceF } from '../../../types/reservation-detail.types'
import useUpdateReservedResourceHook from 'src/app/hooks/update-reserved-resource/update-reserved-resource.hook'
import { UpdateReservedResourceOutput } from 'src/app/hooks/update-reserved-resource/update-reserved-resource.types'

type CheckOutValidation =
  | {
      nextAction: 'CLOSE_BILLS' | 'EARLY_DEPARTURE'
      bills?: BillF[]
      deposits?: DepositF[]
      productionsAssigned?: ProductionAssignedFragment[]
    }
  | {
      nextAction: 'ERROR'
      errors: string[]
    }
  | {
      nextAction: 'CHECKOUT' | 'CONFIRM_EARLY_DEPARTURE'
    }

export default function useCheckoutApi() {
  const { t } = useTranslation()
  const [checkOutMutation, { loading: checkoutLoading }] = useReservedResourcesCheckOutMutation()
  // const [reservedResourceUpdateMutation, { loading: rrUpdateLoading }] = useReservedResourceUpdateMutation()
  const { updateReservedResourceMutation, updateRRMutationLoading } = useUpdateReservedResourceHook({ refetchQueries: ['ReservationDetail'] })
  const hotelDate = useHotelDate()

  const [loading, setLoading] = useState(false)

  const areThereAnyOpenBills = useCallback(
    (reservation: ReservationF, bills: BillF[], reservedResoureIds: string[]) => {
      const reservedResourcesSelected = reservation.reservedResources?.filter((rr) => reservedResoureIds.includes(rr.id)) || []
      const billsRelatedToSelectedResources = bills.filter((b) => {
        if (b.reservedResourceId && b.reservedResourceId !== null) {
          return reservedResourcesSelected.findIndex((rr) => b.reservedResourceId?.includes(rr.id)) > -1
        } else if (b.reservationId && b.reservationId !== null) {
          return reservedResourcesSelected.findIndex((rr) => b.reservationId?.includes(rr.reservationId)) > -1
        } else {
          return false
        }
      })
      return billsRelatedToSelectedResources.filter((b) => b.open && b.productions?.length && b.productions?.length > 0)
    },
    [hotelDate],
  )

  const validateCheckOut = useCallback(
    (reservation: ReservationF, bills: BillF[], deposits: DepositF[], reservedResoureIds: string[]): CheckOutValidation => {
      const reservedResourcesSelected = reservation.reservedResources?.filter((rr) => reservedResoureIds.includes(rr.id)) || []

      // Selezione di almeno una reserved resource - Notifica errori
      if (reservedResourcesSelected.length <= 0) {
        return { nextAction: 'ERROR', errors: [t('pms.booking.reservation.check_out_error.no_selected_reserved_resource')] }
      }

      // Verifica partenza anticipata
      const isEarlyDeparture = reservedResourcesSelected.some((rr) => compareDates(rr.end, hotelDate) > 0)

      const billsOpen = areThereAnyOpenBills(reservation, bills, reservedResoureIds) // billsRelatedToSelectedResources.filter((b) => b.open)
      // Ci sono conti aperti?
      if (billsOpen.length > 0) {
        // Se ci sono conti aperti associati alle reserved resource selezionate, seleziona tutti gli addebidi riferiti alle reserved resource
        // FIXME: se i conti sono legati a più di una prenotazione non bisogna passare il riferimento al conto ma la lista di productionAssigned riferite alla prenotazione (reservation).
        const billsForDoc: BillF[] = []
        const productionAssignedForDoc: ProductionAssignedFragment[] = []

        billsOpen.map((b) => {
          if (b.reservationId && b.reservationId?.length > 1) {
            // Conto relativo a più prenotazioni // TODO: Al momento non esiste un modo per legare un conto a più prenotazioni. Quando verrà implementato occorrerà verificare la logica seguente.
            b.productions?.map((pa) => {
              if (pa.production.reservedResource && reservedResoureIds.includes(pa.production.reservedResource.id)) {
                productionAssignedForDoc.push(pa)
              }
            })
          } else {
            // Seleziona l'intero conto.
            billsForDoc.push(b)
          }
        })
        return {
          nextAction: isEarlyDeparture ? 'EARLY_DEPARTURE' : 'CLOSE_BILLS',
          bills: billsForDoc,
          productionsAssigned: productionAssignedForDoc,
          deposits: deposits,
        }
      }

      if (isEarlyDeparture) {
        return { nextAction: 'CONFIRM_EARLY_DEPARTURE' }
      }
      // Se tutti i conti associati alle reserved resource selezionate sono chiusi effattua il checkout.
      return { nextAction: 'CHECKOUT' }
    },
    [hotelDate, areThereAnyOpenBills],
  )

  const checkOut = (tenantId: string, propertyId: string, reservationId: string, reservedResourceIds: string[]) => {
    checkOutMutation({
      variables: {
        input: {
          tenantId: tenantId,
          reservedResources: reservedResourceIds.map<CheckinCheckoutReservedResourceInput>((rrId) => ({ reservedResourceId: rrId, guestIds: [] })),
        },
      },
      refetchQueries: ['ReservationDetail'],
    })
  }

  type changeReservedResourceDepartureResponse =
    | {
        response: 'SUCCESS'
      }
    | {
        response: 'FAILED'
      }

  const changeReservedResourceDeparture = async (
    tenantId: string,
    reservedResourcesSelected: ReservedResourceF[],
    departureDate: Date,
    onCompleted: () => void,
  ): Promise<changeReservedResourceDepartureResponse> => {
    const responses = reservedResourcesSelected.map(async (rr) => {
      const resourceOccupancies = rr.resourceOccupancies.map(
        (ro) => ({ start: ro.start, end: departureDate, resourceTypeId: ro.reservedResourceType.id, resourceId: ro.resource?.id } as ResourceOccupancyInput),
      )
      const guestOccupancies = rr.guestOccupancies?.map(
        (go) => ({ guestId: go.guest.id, start: go.start, end: departureDate, type: go.type, cityTaxExoneration: go.cityTaxExoneration } as GuestOccupancyInput),
      )

      const updatedManualDailyPrices = rr.manualDailyPrices?.filter((p) => compareDates(p.day, departureDate) < 0)

      const res = updateReservedResourceMutation({
        reservedResourceId: rr.id,
        input: {
          tenantId: tenantId,
          reservationId: rr.reservationId,
          adults: rr.adults,
          children: rr.children,
          chargeCategoryId: rr.chargeCategory.id,
          rateId: rr.rate.id,
          resourceOccupancies,
          guestOccupancies,
          manualDailyPrices: updatedManualDailyPrices,
        },
      })

      return res
    })

    const awaitedResult = await Promise.all(responses)
    if (awaitedResult.every((r) => r.result === 'SUCCESS')) {
      onCompleted()
      return { response: 'SUCCESS' }
    } else {
      return { response: 'FAILED' }
    }
  }

  useEffect(() => {
    setLoading(checkoutLoading || updateRRMutationLoading)
  }, [checkoutLoading, updateRRMutationLoading])

  return { validateCheckOut, checkOut, changeReservedResourceDeparture, areThereAnyOpenBills, loading }
}
